import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import type {
  AppPrefillState,
  AvatarMaterialDto,
  DeliverableDto,
  HandoverAutomationDto,
  Locale,
  MilestoneDto,
  PartnerDto,
  ResourceDto,
  SalespageDeliveryDto,
  SalesStepDto,
  SectionDto
} from "../../shared/dto";
import type { RailStep, RailStepKey, RailTarget } from "../rail-model";
import type { WalkPoint } from "../WalkFeedback";
import { workflowPillClass } from "../workflow-pill";
import type { ActionIssue } from "../action-error";
import { actionIssueLocaleKey } from "../action-error";
import { parseStaffOverrides } from "../../shared/staff-override";
import { intlLanguage } from "../date-format";

export type M5bSurface =
  | "sales"
  | "strategy"
  | "salespage"
  | "emails"
  | "webinar"
  | "publish"
  | "stripe"
  // F171: "Napojení plateb", only for a client with the payment setup.
  | "payments"
  | "handover"
  // F135: the three app steps are three surfaces. `app` is the legacy deep
  // link and lands on the first of them (the Apple account).
  | "app"
  | "app:apple"
  | "app:questionnaire"
  | "app:release";
export type ClientAction = "bot-accept" | "apple-start" | "app-interest";

export type DeliverySectionContentProps = {
  surface: M5bSurface;
  sections: SectionDto[];
  deliverables: DeliverableDto[];
  avatarMaterial?: AvatarMaterialDto | null;
  salespageDelivery?: SalespageDeliveryDto | null;
  salesSteps: SalesStepDto[];
  // F110: what the client is told while a Předání fact is on its way from the
  // Mac Mini. Absent once both facts are in place.
  handoverAutomation?: HandoverAutomationDto | null;
  /** F135b: the ONE server fact about a machine prefill of the app wizard. */
  appPrefill?: AppPrefillState | null;
  onReloadData?: () => Promise<void>;
  resources: ResourceDto[];
  milestones: MilestoneDto[];
  includesApp: boolean;
  /**
   * THIS client's journey (F143, N10). The sales overview numbers its rows and
   * decides which rows exist from the rail, never from a literal - a client
   * whose journey has no chapter 1 starts the sales chapter at step 1, and a
   * sales_only client has no Předání row at all.
   */
  railSteps: ReadonlyArray<RailStep>;
  membershipRole: "owner" | "collaborator";
  partner: PartnerDto | null;
  profilePhotoUrl: string | null;
  locale: Locale;
  t: (key: string) => string;
  // K04: the sales overview lists RAIL steps, two of which (marketing video,
  // sales questionnaire) live outside the delivery surfaces. Typing the
  // callback to the delivery surfaces alone is what forced those two rows to
  // carry a null destination and render as dead controls.
  onNavigate?: (target: RailTarget) => void;
  testMode?: boolean;
  brokenPreviewIds?: Set<string>;
  busyAction: string | null;
  actionError: ActionIssue | null;
  feedbackRevision?: number;
  dataErrors?: Set<string>;
  onPreviewError?: (deliverableId: string) => void;
  onAction: (action: ClientAction, payload?: Record<string, unknown>) => void | Promise<void>;
  onGateApprove?: (deliverableId: string) => Promise<boolean> | boolean;
  onGateFeedback?: (deliverableId: string, payload: { text?: string | null; audioAttachmentId?: string | null; pointerTrace?: WalkPoint[] }) => Promise<boolean> | boolean;
  csrfToken?: string;
  onChanged?: () => Promise<void>;
};

/**
 * Refresh EVERY source the surrounding step derives its mark from (F118).
 *
 * `onChanged` reloads the HOME payload: sections and the rail. But the marks
 * of this ladder - the handover's three steps, the app track's Apple step -
 * are derived from RESOURCES (`resource(props.resources, ...)`), and only
 * `onReloadData` refetches those. A client action that reported through
 * `onChanged` alone therefore stored its fact on the server and changed
 * nothing on screen; the step redrew whenever the window-focus listener next
 * fired, which is what Pavel measured as "a few tens of seconds" and read,
 * correctly, as a dead button.
 *
 * Both reads are best effort and INDEPENDENT: the fact is already stored, so
 * one failed refetch must not hide the other or turn a successful action into
 * a reported failure. A read that really failed is reported by the surface's
 * own `dataErrors` band, which is where a failed read belongs.
 */
export function refreshClientFact(
  props: Pick<DeliverySectionContentProps, "onChanged" | "onReloadData">
): Promise<void> {
  return Promise.allSettled([
    Promise.resolve().then(() => props.onChanged?.()),
    Promise.resolve().then(() => props.onReloadData?.())
  ]).then(() => undefined);
}

export function InlineLockIcon() {
  return (
    <svg className="inline-lock-icon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7.5 10V7.5a4.5 4.5 0 0 1 9 0V10m-10 0h11a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-11a1 1 0 0 1-1-1v-8a1 1 0 0 1 1-1Z" />
    </svg>
  );
}

// K05: ONE preview of a step nobody can open yet - what will appear here, who
// unlocks it, when. The four sales pages already had this anatomy, but the
// client never reached it: the P0 lock gate (fix-nlocks) renders the shared
// locked surface FIRST, so a locked step showed a heading, one sentence and
// ~900 px of nothing. The copy lives under `rail.lockedPreview.<step>` for
// EVERY rail step and both surfaces read it, so the client meets the same
// sentences wherever they land and a second vocabulary cannot grow back.
export function LockedStepPreview(props: { step: RailStepKey; t: (key: string) => string }) {
  return (
    <div className="locked-preview" data-locked-preview-step={props.step}>
      <p className="card-kicker">{props.t("sales.lockedPreview.title")}</p>
      <ul>
        {[1, 2, 3].map((index) => (
          <li className="locked-preview-item" key={index}>{props.t(`rail.lockedPreview.${props.step}.${index}`)}</li>
        ))}
      </ul>
      <dl>
        <div><dt>{props.t("sales.lockedPreview.owner")}</dt><dd>{props.t(`rail.lockedPreview.${props.step}.owner`)}</dd></div>
        <div><dt>{props.t("sales.lockedPreview.when")}</dt><dd>{props.t(`rail.lockedPreview.${props.step}.when`)}</dd></div>
      </dl>
    </div>
  );
}

// F104: the partner returned this material to the client - the client reads
// the WRITTEN reason on the material itself. The note lives in the
// staff_overrides resource; display gates on the LIVE state (the caller only
// renders it beside a material back in review), so a stale note cannot lie.
export function staffReturnedReason(
  resources: Array<{ key: string; value: Record<string, unknown> }>,
  kind: "strategy" | "salespage" | "emails" | "webinar" | "profile" | "apple"
): string | null {
  const entry = parseStaffOverrides(
    resources.find((row) => row.key === "staff_overrides")?.value
  )[kind];
  return entry?.action === "reopened" ? entry.reason : null;
}

// D21 merges legacy audit + plan into one strategy reader and one approval.
// Every reviewable sales document uses the same end-of-reader approval gate;
// the sales page keeps the client's brand design inside its iframe.
// F105 removed the business-proof table (COMPLETION_PROOFS) from the handover
// screen: the handover closes on its own two steps now, and the milestones
// stay data for Servis, never a client-facing checklist.

export type FocusedSalesKey = "strategy" | "salespage" | "emails" | "webinar";

export function ReviewLoopIntro(props: {
  material: FocusedSalesKey;
  placement: "step" | "reader";
  /** Strategy is the gate that opens the rest of the sales materials. */
  unlocksNext?: boolean;
  t: (key: string) => string;
}) {
  const material = props.t(`sales.reviewLoop.material.${props.material}`);
  return (
    <section className={`review-loop-intro ${props.placement}`} data-review-loop-material={props.material}>
      <h2>{props.t("sales.reviewLoop.intro").replace("{document}", material)}</h2>
      <ol>
        <li>
          <span aria-hidden="true">1</span>
          <div>
            <strong>{props.t("sales.reviewLoop.commentsTitle")}</strong>
            <p>{props.t("sales.reviewLoop.commentsCopy")}</p>
          </div>
        </li>
        <li>
          <span aria-hidden="true">2</span>
          <div>
            <strong>{props.t("sales.reviewLoop.approveTitle")}</strong>
            <p>{props.t("sales.reviewLoop.approveCopy")}</p>
          </div>
        </li>
      </ol>
      {/* What approval actually DOES. The old second box said it in other
          words a screen lower; the consequence belongs to the action. */}
      {props.unlocksNext && (
        <p className="review-loop-unlocks">{props.t("sales.reviewLoop.unlocks")}</p>
      )}
    </section>
  );
}

interface FeedbackSummary {
  total: number;
  implemented: number;
  waiting: number;
}

export function feedbackSummaryState(summary: FeedbackSummary): {
  state: "hidden" | "waiting" | "partial" | "implemented";
} {
  if (summary.total <= 0) return { state: "hidden" };
  if (summary.waiting <= 0 && summary.implemented >= summary.total) return { state: "implemented" };
  if (summary.implemented > 0) return { state: "partial" };
  return { state: "waiting" };
}

export function FeedbackStatusSummary(props: {
  summary: FeedbackSummary;
  t: (key: string) => string;
}) {
  const { state } = feedbackSummaryState(props.summary);
  if (state === "hidden") return null;
  const copy = props.t(`sales.feedbackSummary.${state}`)
    .replace("{total}", String(props.summary.total))
    .replace("{implemented}", String(props.summary.implemented))
    .replace("{waiting}", String(props.summary.waiting));
  return (
    <section className={`feedback-status-summary ${state}`} data-feedback-summary-state={state} role="status" aria-live="polite">
      <span className="feedback-status-summary-mark" aria-hidden="true" />
      <div>
        <p>{copy}</p>
        <dl>
          <div><dt>{props.t("sales.feedbackSummary.totalLabel")}</dt><dd>{props.summary.total}</dd></div>
          <div><dt>{props.t("sales.feedbackSummary.implementedLabel")}</dt><dd>{props.summary.implemented}</dd></div>
          <div><dt>{props.t("sales.feedbackSummary.waitingLabel")}</dt><dd>{props.summary.waiting}</dd></div>
        </dl>
      </div>
    </section>
  );
}

export function MaterialFeedbackSummary(props: {
  deliverableId: string;
  version: number;
  refreshVersion?: number;
  onVersionAdvanced?: () => void | Promise<void>;
  t: (key: string) => string;
}) {
  const [summary, setSummary] = useState<FeedbackSummary | null>(null);
  const [alexActive, setAlexActive] = useState(false);
  const [failed, setFailed] = useState(false);
  const requestRef = useRef<AbortController | null>(null);
  const load = useCallback(async () => {
    requestRef.current?.abort();
    const controller = new AbortController();
    requestRef.current = controller;
    try {
      const response = await fetch(`/api/deliverables/${props.deliverableId}/feedback`, {
        credentials: "include",
        signal: controller.signal
      });
      const body = await response.json() as {
        ok?: boolean;
        summary?: FeedbackSummary;
        alexActive?: boolean;
        currentDeliverableId?: string;
      };
      if (!response.ok || body.ok !== true || !body.summary) throw new Error("feedback_summary_failed");
      setSummary(body.summary);
      setAlexActive(Boolean(body.alexActive));
      setFailed(false);
      if (body.currentDeliverableId && body.currentDeliverableId !== props.deliverableId) {
        void props.onVersionAdvanced?.();
      }
    } catch (error) {
      if ((error as { name?: string }).name !== "AbortError") setFailed(true);
    } finally {
      if (requestRef.current === controller) requestRef.current = null;
    }
  }, [props.deliverableId, props.onVersionAdvanced]);

  useEffect(() => {
    setSummary(null);
    setAlexActive(false);
    void load();
    return () => requestRef.current?.abort();
  }, [load, props.refreshVersion, props.version]);

  useEffect(() => {
    if (!alexActive && (!summary || summary.total <= 0 || summary.waiting <= 0)) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") void load();
    }, alexActive ? 8000 : 15_000);
    const onFocus = () => { void load(); };
    window.addEventListener("focus", onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [alexActive, load, summary]);

  if (failed && !summary) {
    return (
      <div className="feedback-summary-retry" role="alert">
        <span>{props.t("sales.feedbackSummary.error")}</span>
        <button className="quiet-link" type="button" onClick={() => void load()}>{props.t("common.retry")}</button>
      </div>
    );
  }
  return summary ? <FeedbackStatusSummary summary={summary} t={props.t} /> : null;
}

export function FocusedSalesPage(props: {
  page: FocusedSalesKey;
  state: SalesStepDto["state"] | null;
  copyKey?: string;
  t: (key: string) => string;
  children: ReactNode;
}) {
  return (
    <section className="m5b-view focused-sales-page" data-focused-sales-page={props.page} aria-labelledby={`${props.page}-title`}>
      <header className="m5b-heading focused-sales-heading">
        <div>
          <p className="eyebrow blue-eyebrow">{props.t(`sales.pages.${props.page}.eyebrow`)}</p>
          <h1 id={`${props.page}-title`}>{props.t(`rail.steps.${props.page}`)}</h1>
          <p className="lead">{props.t(props.copyKey ?? `sales.pages.${props.page}.copy`)}</p>
        </div>
        <span className={`status-pill ${workflowPillClass(props.state)}`}>
          {props.state ? props.t(`states.${props.state}`) : props.t("sales.overview.unavailable")}
        </span>
      </header>
      {props.state === null ? (
        <article className="focused-sales-lock data-unavailable" role="status">
          <span aria-hidden="true">!</span>
          <p>{props.t("sales.pages.unavailable")}</p>
        </article>
      ) : props.state === "locked" ? (
        <article className="focused-sales-lock" data-locked-preview="true" role="status">
          <p className="inline-lock-copy"><InlineLockIcon />{props.t(`sales.pages.${props.page}.locked`)}</p>
          <LockedStepPreview step={props.page} t={props.t} />
        </article>
      ) : (
        <>
          {props.state === "done" && (
            <article className="focused-sales-recap">
              <div>
                <p className="card-kicker">{props.t("sales.doneRecap.eyebrow")}</p>
                <p>{props.t(`sales.pages.${props.page}.recap`)}</p>
              </div>
              <a className="quiet-link" href={`#${props.page}-content`}>{props.t("sales.doneRecap.open")}</a>
            </article>
          )}
          <div className="focused-sales-body" id={`${props.page}-content`}>{props.children}</div>
        </>
      )}
    </section>
  );
}

export function focusedState(
  props: DeliverySectionContentProps,
  key: FocusedSalesKey
): SalesStepDto["state"] | null {
  if (props.dataErrors?.has("deliverables")) return null;
  return props.salesSteps.find((item) => item.key === key)?.state ?? null;
}

// The kicker above the title already names the kind; generated titles often
// arrive as "Audit: Hlídač čísel". One human title per tile (W3 law): strip
// the redundant kind prefix, keep everything else verbatim.
export function humanDocTitle(title: string, kindLabel: string): string {
  const prefix = `${kindLabel}:`;
  if (title.toLowerCase().startsWith(prefix.toLowerCase())) {
    const rest = title.slice(prefix.length).trim();
    if (rest) return rest;
  }
  return title;
}

export function DataError({ t }: { t: (key: string) => string }) {
  return <p className="message error-message" role="alert">{t("sales.loadError")}</p>;
}

/**
 * N03: an action that was REFUSED names the refusal. The server already knows
 * (`too_many_accounts`, `capacity_reached`, `state_conflict`, ...); the only
 * reason the client used to say "zkus to znovu" is that the code was dropped
 * on the way. `issue` is null when nothing failed.
 */
export function ActionError({ t, issue }: { t: (key: string) => string; issue: ActionIssue | null }) {
  if (!issue) return null;
  return <p className="message error-message" role="alert">{t(actionIssueLocaleKey(issue))}</p>;
}

export function resource(resources: ResourceDto[], key: ResourceDto["key"]): ResourceDto | null {
  return resources.find((item) => item.key === key) ?? null;
}

export function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

export function formatDate(value: string, locale: Locale): string {
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return value;
  return new Intl.DateTimeFormat(intlLanguage(locale), {
    dateStyle: "medium",
    timeZone: "Europe/Prague"
  }).format(date);
}
